/**
 * Data for the public site: aggregate, cached, sign-in-free endpoints
 * (src/rai/api/routes/public.py) plus the public plans list.
 * Every field may be null: the page shows what exists and hides the rest.
 */
import { useQuery } from "@tanstack/react-query";

import { request } from "@/api/client";
import {
  planOffer,
  type PlanOffer,
  type PlansResponse,
} from "@/pages/account/platform";

import { raPlans } from "./raPlanData";

export interface SnapshotIndex {
  name: string;
  as_of?: string | null;
  close: number | null;
  change_pct: number | null;
  spark: number[];
}

export interface PublicMarketSnapshot {
  as_of: string | null;
  indices: SnapshotIndex[];
  vix: {
    as_of: string | null;
    close: number | null;
    change_pct: number | null;
    percentile_1y: number | null;
  } | null;
  breadth: {
    as_of: string | null;
    advances: number | null;
    declines: number | null;
    pct_above_200dma: number | null;
    pct_above_50dma: number | null;
    new_highs: number | null;
    new_lows: number | null;
  } | null;
  flows: {
    as_of: string | null;
    fii_net: number | null;
    dii_net: number | null;
  } | null;
  nifty_series?: { date: string; close: number }[] | null;
  generated_at?: string | null;
}

export interface ShowcaseSignal {
  signal: string;
  label: string | null;
  horizon_days: number | null;
  ic_mean: number | null;
  ic_ir: number | null;
  t_stat?: number | null;
  kind?: string | null;
  universe?: string | null;
}

export interface PublicShowcase {
  generated_at?: string | null;
  data: {
    equity_rows: number | null;
    fo_rows: number | null;
    price_rows: number | null;
    symbols: number | null;
    first_date: string | null;
    last_date: string | null;
    index_days: number | null;
    fundamentals_filings: number | null;
    stocks_with_fundamentals: number | null;
  } | null;
  register: {
    ras_total: number | null;
    cities: number | null;
    states: number | null;
    pincode_matched: number | null;
    on_platform: number | null;
  } | null;
  survivorship: {
    source: "backtest_runs" | "docs" | string;
    pit_cagr: number | null;
    survivor_cagr: number | null;
    nifty_cagr: number | null;
    period_start: string | null;
    period_end: string | null;
    walk_forward_oos_cagr: number | null;
    walk_forward_is_cagr: number | null;
    strategy?: string | null;
    note?: string | null;
  } | null;
  costs: { name: string; detail: string }[] | null;
  signals: {
    run_id?: number | null;
    as_of: string | null;
    top: ShowcaseSignal[];
  } | null;
  fno: {
    symbol: string;
    as_of: string | null;
    expiry: string | null;
    atm_iv: number | null;
    iv_rank: number | null;
    iv_percentile: number | null;
    pcr: number | null;
    max_pain: number | null;
    spot?: number | null;
    days_to_expiry?: number | null;
  } | null;
  scan: {
    expression: string;
    universe: string | null;
    matches: number | null;
    as_of: string | null;
    description?: string | null;
  } | null;
  ai: {
    status: string;
    oos_ic: number | null;
    oos_t: number | null;
    note?: string | null;
  } | null;
}

const MINUTE = 60_000;

export function useMarketSnapshot() {
  return useQuery({
    queryKey: ["/public/market-snapshot", null, null] as const,
    queryFn: ({ signal }) =>
      request<PublicMarketSnapshot>("get", "/public/market-snapshot", {
        signal,
      }),
    staleTime: MINUTE,
    refetchInterval: MINUTE,
    retry: 1,
  });
}

export function useShowcase() {
  return useQuery({
    queryKey: ["/public/showcase", null, null] as const,
    queryFn: ({ signal }) =>
      request<PublicShowcase>("get", "/public/showcase", { signal }),
    staleTime: 10 * MINUTE,
    retry: 1,
  });
}

export function usePlans() {
  return useQuery({
    queryKey: ["/platform/plans", null, null] as const,
    queryFn: ({ signal }) =>
      request<PlansResponse>("get", "/platform/plans", { signal }),
    staleTime: 10 * MINUTE,
    retry: 1,
  });
}

/** A Research Analyst verified on the platform (GET /public/platform-ras). */
export interface PlatformRa {
  name: string;
  reg_no: string;
  city: string | null;
  state: string | null;
  /** The public profile page, /research-analyst/<slug>. */
  profile_path: string;
  /** The approved photo's key; null: initials. */
  photo: string | null;
  featured: boolean;
  followers: number;
  /** Calls published and open now (facts, not performance: SEBI keeps hit rates off the home page). */
  calls?: number;
  open_calls?: number;
  /** Profile page views (one per visitor per day). */
  profile_views?: number;
}

export function usePlatformRas() {
  return useQuery({
    queryKey: ["/public/platform-ras", null, null] as const,
    queryFn: ({ signal }) =>
      request<{ ras: PlatformRa[] }>("get", "/public/platform-ras", { signal }),
    staleTime: 5 * MINUTE,
    retry: 1,
  });
}

/**
 * The Research Analyst plans' launch offer (Admin → Plans & trials), or null when no
 * RA plan has one: every "launch offer" wording follows it, never a fixed "50% off".
 */
export function useRaOffer(): PlanOffer | null {
  const plans = usePlans();
  return (
    raPlans(plans.data)
      .map((p) => planOffer(p))
      .find((o) => o !== null) ?? null
  );
}
